import type z from "zod";
import { defaults } from "@/exports/client";
import type { PluginClientConfig, PluginClientDefinition } from "@/plugins/client/types";
import { toMethodName } from "@/shared/method-name";
import type { Override } from "@/shared/types";
import type { AgentDefinition } from "../server/types";
import type { AgentClientDefinition, TypedAgentClientBuilder } from "./types";

export class AgentClientBuilder<
  const AgentClientDef extends AgentClientDefinition,
  Excluded extends string = never,
> {
  readonly def: AgentClientDef;

  constructor(definition: AgentClientDef) {
    this.def = definition;
  }

  /**
   * Register plugins to extend the agent client features.
   * Defaults to `[generation, memories, stores, actions, percepts]` plugins if not specified.
   *
   * In case you want to register custom plugins and still keep the defaults you can do:
   * ```ts
   * import { defaults } from "life/server";
   *
   * defineAgent("my-agent").plugins([...defaults.plugins, myCustomPlugin]);
   * ```
   *
   * Or if you want only some of the defaults, you can do:
   * ```ts
   * import { defaults } from "life/server";
   *
   * defineAgent("my-agent").plugins([defaults.plugins.generation, defaults.plugins.memories]);
   * ```
   */
  plugins<const Plugins extends { def: PluginClientDefinition }[]>(plugins: Plugins) {
    const builder = new AgentClientBuilder({ ...this.def, plugins: plugins.map((p) => p.def) });
    const builderWithPlugins = AgentClientBuilder.withPluginsMethods(builder);
    return builderWithPlugins as TypedAgentClientBuilder<
      Override<
        Override<
          AgentClientDef,
          "plugins",
          {
            [K in keyof Plugins]: Plugins[K]["def"];
          }
        >,
        "pluginConfigs",
        {
          [K in Plugins[number] as K["def"]["name"]]: PluginClientConfig<
            K["def"]["config"],
            "output"
          >;
        }
      >,
      Excluded | "plugins"
    >;
  }

  // biome-ignore lint/suspicious/noExplicitAny: reason
  static withPluginsMethods<Builder extends AgentClientBuilder<any, any>>(builder: Builder) {
    for (const plugin of builder.def.plugins) {
      Object.assign(builder, {
        [toMethodName(plugin.name)]: (
          config: z.input<PluginClientDefinition["config"]["schema"]>,
        ): unknown => {
          const newBuilder = new AgentClientBuilder({
            ...builder.def,
            pluginConfigs: {
              ...(builder.def.pluginConfigs ?? {}),
              [plugin.name]: config,
            },
          });
          return AgentClientBuilder.withPluginsMethods(newBuilder);
        },
      });
    }
    return builder;
  }
}

// Helper function to define a agent client
export function defineAgentClient<const ServerAgent extends { def: AgentDefinition }>(
  name: ServerAgent["def"]["name"],
) {
  const defaultDefinition = {
    name,
    plugins: [...defaults.plugins].map((p) => p.def),
    pluginConfigs: {},
    $serverDef: {} as ServerAgent["def"],
  } as const satisfies AgentClientDefinition;
  const builder = new AgentClientBuilder(defaultDefinition);
  const builderWithPlugins = AgentClientBuilder.withPluginsMethods(builder);
  return builderWithPlugins as TypedAgentClientBuilder<typeof builderWithPlugins.def, never>;
}
